import { Link } from "react-router-dom";

/**
 * Internal-linking section surfaced on the Home and Features pages. Links the
 * newer feature/security pages and blog guides from high-authority pages so
 * they're discoverable to visitors and crawlers (they were previously only
 * reachable from the footer). Labels are English because every target here is
 * English-only content.
 */

const features = [
  {
    to: "/bookmarks-for-schools",
    icon: "bi-mortarboard",
    title: "For Schools & Students",
    description:
      "The best way to save favorites for school: organize links by class and subject, free and unlimited.",
  },
  {
    to: "/bookmarks-for-teachers",
    icon: "bi-easel",
    title: "For Teachers",
    description:
      "Collect lesson resources by class, keep each prep in its own Space, and reuse your library year after year.",
  },
  {
    to: "/security",
    icon: "bi-shield-lock",
    title: "Security",
    description:
      "Zero-knowledge encryption, PBKDF2 key derivation, and exactly how we protect your data.",
  },
  {
    to: "/features/offline",
    icon: "bi-wifi-off",
    title: "Offline Mode",
    description:
      "Browse your library and save new bookmarks with no connection. Changes sync when you're back.",
  },
  {
    to: "/features/duplicate-detection",
    icon: "bi-files",
    title: "Duplicate Detection",
    description:
      "Get warned before you save a link you already have, and skip duplicates on bulk import.",
  },
];

const guides = [
  {
    to: "/blog/best-way-to-save-favorites-for-school",
    title: "The Best Way to Save Favorites for School",
    description: "A favorites system that survives a full course load — set up in minutes.",
  },
  {
    to: "/blog/save-favorites-school-chromebook",
    title: "Save & Organize Favorites on a School Chromebook",
    description: "Turn a managed Chromebook into a real organizer for Classroom and Canvas links.",
  },
  {
    to: "/blog/how-to-organize-bookmarks-into-collections",
    title: "How to Organize Bookmarks into Collections",
    description: "A collections-and-tags system that scales past a few hundred bookmarks.",
  },
  {
    to: "/blog/organize-bookmarks-for-students",
    title: "How to Organize Bookmarks for Students",
    description: "A setup that survives a full semester of sources and deadlines.",
  },
  {
    to: "/blog/automatically-tag-bookmarks",
    title: "Automatically Tag Posts and Pages You Save",
    description: "Let consistent, content-based tags write themselves.",
  },
];

export const ExploreMore = () => {
  return (
    <section className="container section-padding">
      <div className="row">
        <div className="col-lg-8 mx-auto text-center mb-4">
          <h2 className="fw-bold mb-2">Explore more</h2>
          <p className="lead text-muted mb-0">
            More of what onBookmarks can do, and guides to get the most out of it.
          </p>
        </div>
      </div>

      <div className="row g-4">
        {features.map((f) => (
          <div key={f.to} className="col-md-4">
            <Link
              to={f.to}
              className="card h-100 border-0 shadow-sm text-decoration-none text-reset p-4"
            >
              <div className="icon icon-lg bg-purple-subtle rounded-3 mb-3">
                <i className={`bi ${f.icon} fs-4 text-purple`}></i>
              </div>
              <h3 className="h5 mb-2">{f.title}</h3>
              <p className="text-muted mb-3">{f.description}</p>
              <span className="text-purple mt-auto">Learn more</span>
            </Link>
          </div>
        ))}
      </div>

      <div className="row mt-5">
        <div className="col-lg-8 mx-auto text-center mb-4">
          <h3 className="h4 fw-bold mb-0">Guides from our blog</h3>
        </div>
      </div>

      <div className="row g-4">
        {guides.map((g) => (
          <div key={g.to} className="col-md-4">
            <Link
              to={g.to}
              className="card h-100 border-0 shadow-sm text-decoration-none text-reset p-4"
            >
              <div className="d-flex align-items-center gap-2 mb-2 text-purple">
                <i className="bi bi-journal-text fs-5"></i>
                <span className="small fw-semibold">Guide</span>
              </div>
              <h4 className="h6 mb-2">{g.title}</h4>
              <p className="text-muted small mb-3">{g.description}</p>
              <span className="text-purple small mt-auto">Read the guide</span>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ExploreMore;
